import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PatientPaginationProps {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  rowSelectionCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: string) => void;
  canPreviousPage: boolean;
  canNextPage: boolean;
  sortBy: string;
  onSortChange: (value: string) => void;
}

const PatientPagination = ({
  currentPage,
  pageSize,
  totalPages,
  rowSelectionCount,
  onPageChange,
  onPageSizeChange,
  canPreviousPage,
  canNextPage,
  sortBy,
  onSortChange,
}: PatientPaginationProps) => {
  return (
    <div className="flex flex-col gap-3 pt-4 md:flex-row md:items-center md:justify-between">
      <div className="flex flex-wrap items-center gap-3">
        {rowSelectionCount > 0 && (
          <p className="text-sm text-muted-foreground">
            {rowSelectionCount} patients selected
          </p>
        )}

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Show:</span>
          <Select value={pageSize.toString()} onValueChange={onPageSizeChange}>
            <SelectTrigger className="w-[70px]">
              <SelectValue>{pageSize}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="125">125</SelectItem>
              <SelectItem value="150">150</SelectItem>
              <SelectItem value="200">200</SelectItem>
              <SelectItem value="300">300</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sort:</span>
          <Select value={sortBy} onValueChange={onSortChange}>
            <SelectTrigger className="w-[150px] md:w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at:desc">Created Date (Newest First)</SelectItem>
              <SelectItem value="created_at:asc">Created Date (Oldest First)</SelectItem>
              <SelectItem value="updated_at:desc">Updated Date (Newest First)</SelectItem>
              <SelectItem value="updated_at:asc">Updated Date (Oldest First)</SelectItem>
              <SelectItem value="first_name:asc">First Name (A-Z)</SelectItem>
              <SelectItem value="first_name:desc">First Name (Z-A)</SelectItem>
              <SelectItem value="last_name:asc">Last Name (A-Z)</SelectItem>
              <SelectItem value="last_name:desc">Last Name (Z-A)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        {/* Page selector dropdown */}
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Page:</span>
            <Select
              value={(currentPage + 1).toString()}
              onValueChange={(value) => onPageChange(parseInt(value) - 1)}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue>{currentPage + 1}</SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (pageNum) => (
                    <SelectItem key={pageNum} value={pageNum.toString()}>
                      {pageNum}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
            <div className="text-sm text-muted-foreground whitespace-nowrap">
              of {totalPages}
            </div>
          </div>
        )}
        <Pagination className="w-auto mx-0">
          <PaginationContent className="flex-wrap">
            <PaginationItem>
              {canPreviousPage ? (
                <PaginationPrevious
                  onClick={() => onPageChange(currentPage - 1)}
                />
              ) : (
                <span className="opacity-50 cursor-not-allowed">
                  <PaginationPrevious onClick={(e) => e.preventDefault()} />
                </span>
              )}
            </PaginationItem>

            {/* Smart pagination with ellipsis */}
            {(() => {
              const pageCount = totalPages;

              // Always show first page
              return (
                <>
                  <PaginationItem>
                    <PaginationLink
                      isActive={currentPage === 0}
                      onClick={() => onPageChange(0)}
                    >
                      1
                    </PaginationLink>
                  </PaginationItem>

                  {/* Show ellipsis if there are many pages and we're not near the start */}
                  {currentPage > 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}

                  {/* Show current page and adjacent pages */}
                  {Array.from({ length: pageCount }, (_, i) => i)
                    .filter(
                      (pageIndex) =>
                        pageIndex !== 0 &&
                        pageIndex !== pageCount - 1 &&
                        Math.abs(pageIndex - currentPage) <= 1
                    )
                    .map((pageIndex) => (
                      <PaginationItem key={pageIndex}>
                        <PaginationLink
                          isActive={pageIndex === currentPage}
                          onClick={() => onPageChange(pageIndex)}
                        >
                          {pageIndex + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ))}

                  {/* Show ellipsis if there are many pages and we're not near the end */}
                  {currentPage < pageCount - 3 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}

                  {/* Always show last page if it exists */}
                  {pageCount > 1 && (
                    <PaginationItem>
                      <PaginationLink
                        isActive={currentPage === pageCount - 1}
                        onClick={() => onPageChange(pageCount - 1)}
                      >
                        {pageCount}
                      </PaginationLink>
                    </PaginationItem>
                  )}
                </>
              );
            })()}

            <PaginationItem>
              {canNextPage ? (
                <PaginationNext onClick={() => onPageChange(currentPage + 1)} />
              ) : (
                <span className="opacity-50 cursor-not-allowed">
                  <PaginationNext onClick={(e) => e.preventDefault()} />
                </span>
              )}
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
};

export default PatientPagination;
